import { Helmet } from 'react-helmet-async'
import { BentoGrid, BentoGridItem } from '../components/ui/BentoGrid'
import GrantaEarthLogo from '../components/ui/GrantaEarthLogo'

export default function Mining() {
  return (
    <>
      <Helmet>
        <title>Mining | GrantaEarth Co.</title>
        <meta name="description" content="Chrome focused mining with claims and tributary agreements in Lalapanzi, Zvishavane, and Mutorashanga." />
      </Helmet>

      <section className="bg-gradient-to-b from-off-white to-white section-padding">
        <div className="container-custom">
          <div className="text-center mb-8">
            <GrantaEarthLogo size="lg" className="mx-auto mb-6" />
          </div>
          
          <h1 className="text-3xl md:text-5xl font-extrabold text-center">Mining</h1>
          <p className="mt-4 text-center text-gray-700 max-w-3xl mx-auto">
            Chrome focused operations supported by disciplined safety practices and reliable offtake contracts.
          </p>

          <div className="mt-10">
            <BentoGrid>
              <BentoGridItem 
                title="Lalapanzi" 
                description="Active chrome operations with access to logistics and processing partners. Strategic location provides competitive transport costs."
                icon="⛏️"
              />
              <BentoGridItem 
                title="Zvishavane" 
                description="Tributary agreements and contract backed supply for priority buyers. Established relationships with local mining communities."
                icon="🤝"
              />
              <BentoGridItem 
                title="Mutorashanga" 
                description="Production planning aligned with environmental and community guidelines. Sustainable mining practices for long-term operations."
                icon="🌱"
              />
            </BentoGrid>
          </div>

          {/* Mining Operations Gallery */}
          <div className="mt-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-charcoal">Our Mining Operations</h2>
              <p className="mt-3 text-gray-600 max-w-2xl mx-auto">
                Authentic views from our chrome mining sites showing active operations, equipment, and high-quality chrome ore production.
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:gap-12">
              {/* Mining Site Overview */}
              <div className="group">
                <div className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <img 
                    src="/images/mining/chrome-mining-site.png" 
                    alt="Chrome mining site overview showing trucks, equipment and stockpiles" 
                    className="w-full h-64 md:h-80 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-white text-xl font-semibold mb-2">Mining Site Operations</h3>
                    <p className="text-white/90 text-sm">
                      Active chrome extraction with heavy machinery, transport trucks, and organized stockpile management at our Zimbabwe mining locations.
                    </p>
                  </div>
                </div>
              </div>

              {/* Chrome Ore Detail */}
              <div className="group">
                <div className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <img 
                    src="/images/mining/chrome-ore-detail.png" 
                    alt="Close-up detail of chrome ore chunks and rocks" 
                    className="w-full h-64 md:h-80 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-white text-xl font-semibold mb-2">Chrome Ore Quality</h3>
                    <p className="text-white/90 text-sm">
                      High-grade chrome ore with consistent quality and sizing, ready for processing and export to international markets.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Mining Operations Images */}
            <div className="mt-8 grid gap-8 md:grid-cols-2 lg:gap-12">
              {/* Mining Equipment */}
              <div className="group">
                <div className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <img 
                    src="/images/mining/chrome-mining-equipment.jpeg" 
                    alt="Heavy mining equipment and machinery at chrome mining site" 
                    className="w-full h-64 md:h-80 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-white text-xl font-semibold mb-2">Heavy Mining Equipment</h3>
                    <p className="text-white/90 text-sm">
                      Modern excavators, loaders, and transport vehicles ensure efficient chrome ore extraction and processing operations.
                    </p>
                  </div>
                </div>
              </div>

              {/* Processing & Loading */}
              <div className="group">
                <div className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <img 
                    src="/images/mining/chrome-processing-loading.png" 
                    alt="Chrome ore processing and loading operations for transport" 
                    className="w-full h-64 md:h-80 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-white text-xl font-semibold mb-2">Processing & Loading</h3>
                    <p className="text-white/90 text-sm">
                      Systematic sorting, processing, and loading operations preparing chrome ore for export to international markets.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Operations Statistics */}
            <div className="mt-12 grid gap-6 sm:grid-cols-3">
              <div className="text-center p-6 rounded-xl border bg-white shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="text-3xl font-extrabold text-deep-green">3</div>
                <div className="mt-1 text-gray-600">Active Sites</div>
              </div>
              <div className="text-center p-6 rounded-xl border bg-white shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="text-3xl font-extrabold text-deep-green">24/7</div>
                <div className="mt-1 text-gray-600">Operations</div>
              </div>
              <div className="text-center p-6 rounded-xl border bg-white shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="text-3xl font-extrabold text-deep-green">100%</div>
                <div className="mt-1 text-gray-600">Compliance</div>
              </div>
            </div>
          </div>

          {/* Mining Portfolio Section */}
          <div className="mt-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-charcoal">Mining Portfolio</h2>
              <p className="mt-3 text-gray-600 max-w-3xl mx-auto">
                Our company is involved in the full value chain from exploration, to mining for gold and chrome.
              </p>
            </div>

            <div className="grid gap-8 lg:grid-cols-2 lg:gap-12">
              {/* Gold Operations */}
              <div className="rounded-2xl border bg-white p-8 shadow-sm hover:shadow-lg transition-shadow duration-300">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-yellow-400 to-yellow-600 flex items-center justify-center text-white text-xl font-bold">
                    Au
                  </div>
                  <h3 className="ml-4 text-2xl font-bold text-charcoal">Gold</h3>
                </div>
                <p className="text-gray-700 mb-4">
                  We are currently developing a medium sized gold mine in Shamva, and by the end of Q4 2025, we project monthly production to be at least 10,000 ounces.
                </p>
                <p className="text-gray-700">
                  Our medium term strategy would be to manufacture high end jewellery, curated for a niche market, that values differentiation.
                </p>
                <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-yellow-800">Projected Monthly Production</span>
                    <span className="text-lg font-bold text-yellow-900">10,000 oz</span>
                  </div>
                  <div className="mt-2 text-xs text-yellow-700">Target: Q4 2025</div>
                </div>
              </div>

              {/* Chrome Operations */}
              <div className="rounded-2xl border bg-white p-8 shadow-sm hover:shadow-lg transition-shadow duration-300">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-deep-green to-forest-green flex items-center justify-center text-white text-xl font-bold">
                    Cr
                  </div>
                  <h3 className="ml-4 text-2xl font-bold text-charcoal">Chrome</h3>
                </div>
                <p className="text-gray-700 mb-4">
                  We own vast mining land along the Great Dyke, and we are currently producing high grade concentrates.
                </p>
                <p className="text-gray-700">
                  Our long-term strategy is to develop a local beneficiation plan that will produce high grade ferro chrome for the export and local steel industry.
                </p>
                <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="text-sm font-medium text-green-800 mb-2">Great Dyke Operations</div>
                  <div className="text-xs text-green-700">High grade concentrates • Local beneficiation • Ferro chrome production</div>
                </div>
              </div>
            </div>

            {/* Strategy Statement */}
            <div className="mt-8 rounded-2xl border bg-gradient-to-r from-deep-green to-forest-green p-8 text-white">
              <h3 className="text-xl font-semibold mb-4">Our Approach</h3>
              <p className="text-green-100">
                Our approach focuses on identifying highly prospective mining assets that can be developed into highly productive mines within the shortest possible time.
              </p>
            </div>
          </div>

          {/* Logistics Capacity Section */}
          <div className="mt-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-charcoal">Our Logistics Capacity</h2>
              <p className="mt-3 text-gray-600 max-w-3xl mx-auto">
                Although Zimbabwe is land locked, our company has developed inland logistics capacity that enhances our ability to minimise the mine to market route.
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {/* Beira Port */}
              <div className="text-center p-6 rounded-xl border bg-white shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="w-12 h-12 rounded-full bg-blue-100 mx-auto mb-4 flex items-center justify-center">
                  <span className="text-blue-600 text-xl">⚓</span>
                </div>
                <h4 className="font-semibold text-charcoal mb-2">Beira</h4>
                <p className="text-sm text-gray-600">Storage hospitality arrangements operational</p>
              </div>

              {/* Maputo Port */}
              <div className="text-center p-6 rounded-xl border bg-white shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="w-12 h-12 rounded-full bg-blue-100 mx-auto mb-4 flex items-center justify-center">
                  <span className="text-blue-600 text-xl">⚓</span>
                </div>
                <h4 className="font-semibold text-charcoal mb-2">Maputo</h4>
                <p className="text-sm text-gray-600">Storage hospitality arrangements operational</p>
              </div>

              {/* Walvis Bay Port */}
              <div className="text-center p-6 rounded-xl border bg-white shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="w-12 h-12 rounded-full bg-blue-100 mx-auto mb-4 flex items-center justify-center">
                  <span className="text-blue-600 text-xl">⚓</span>
                </div>
                <h4 className="font-semibold text-charcoal mb-2">Walvis Bay</h4>
                <p className="text-sm text-gray-600">Storage hospitality arrangements operational</p>
              </div>

              {/* Richards Bay Port */}
              <div className="text-center p-6 rounded-xl border bg-white shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="w-12 h-12 rounded-full bg-orange-100 mx-auto mb-4 flex items-center justify-center">
                  <span className="text-orange-600 text-xl">🚧</span>
                </div>
                <h4 className="font-semibold text-charcoal mb-2">Richards Bay</h4>
                <p className="text-sm text-gray-600">In development • Q1 2026 target</p>
              </div>
            </div>

            <div className="mt-8 rounded-2xl border bg-blue-50 p-8">
              <h3 className="text-xl font-semibold text-blue-900 mb-4">Multi-Port Strategy</h3>
              <p className="text-blue-800">
                Through our networks we are able to use multiple ports of exit along the high seas bordering Southern Africa. 
                We are currently developing the Richards Bay route, and by the end of Q1 2026, we will be positioned to ship from the port.
              </p>
            </div>
          </div>

          <div className="mt-10 rounded-2xl border bg-white p-8 shadow-sm hover:shadow-md transition-shadow duration-300">
            <h2 className="text-xl font-semibold">Safety and Environment</h2>
            <p className="mt-3 text-gray-700">
              Our model prioritizes safe working conditions, environmental care, and compliance. Reporting rhythms provide counterparties with production and quality visibility.
            </p>
          </div>
        </div>
      </section>
    </>
  )
}
